// Drawer « Voir les preuves » — affiche la liste evidence + décideur
// telle qu'elle sort du backend UnifiedLead. Zéro invention.
import { X, ExternalLink, ShieldCheck } from "lucide-react";
import { NAVY, CHAMPAGNE, SLATE, IVORY, GOLD } from
  "@/components/altiora/PremiumUI";
import { humanSignal, humanSource } from "@/lib/altiora-leads-i18n";

export default function EvidenceDrawer({ lead, onClose }) {
  if (!lead) return null;
  const evidence = lead.evidence || [];
  const dm = lead.decision_maker || {};
  const contact = lead.contactability || {};

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="evidence-drawer">
      <div className="flex-1 bg-black/40" onClick={onClose}
           data-testid="evidence-backdrop" />
      <aside
        className="w-full sm:w-[520px] h-full overflow-y-auto shadow-2xl"
        style={{ background: IVORY }}>
        <header className="sticky top-0 px-6 py-4 flex items-center gap-3"
                style={{ background: NAVY, color: IVORY }}>
          <ShieldCheck className="h-4 w-4" style={{ color: GOLD }} />
          <div className="flex-1">
            <div className="text-[10px] font-mono uppercase tracking-[0.24em]"
                 style={{ color: GOLD }}>
              Preuves collectées
            </div>
            <h3 className="font-serif text-[18px]">{lead.entity_name}</h3>
          </div>
          <button onClick={onClose} data-testid="evidence-close"
                  className="p-1 rounded hover:bg-white/10">
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="p-6 space-y-6">
          {/* Contactabilité — bloc obligatoire */}
          <section data-testid="evidence-contactability">
            <h4 className="text-[11px] font-mono uppercase tracking-[0.22em] mb-2"
                style={{ color: SLATE }}>
              Cadre de sollicitation
            </h4>
            <div className="rounded-lg p-4 bg-white"
                 style={{ border: `1px solid ${CHAMPAGNE}44` }}>
              <div className="text-[13px]" style={{ color: NAVY }}>
                Statut backend : <span className="font-mono">{contact.status || "unknown"}</span>
              </div>
              {(contact.channels_allowed || []).length > 0 && (
                <div className="mt-2 text-[12px]" style={{ color: SLATE }}>
                  Canaux autorisés :{" "}
                  <span className="font-mono">
                    {(contact.channels_allowed || []).join(", ")}
                  </span>
                </div>
              )}
              {contact.restriction_reason
                && contact.restriction_reason !== "not_found" && (
                <div className="mt-2 text-[12px]" style={{ color: NAVY }}>
                  Raison : {contact.restriction_reason}
                </div>
              )}
            </div>
          </section>

          {/* Décideur */}
          <section data-testid="evidence-decision-maker">
            <h4 className="text-[11px] font-mono uppercase tracking-[0.22em] mb-2"
                style={{ color: SLATE }}>
              Décideur rattaché
            </h4>
            <div className="rounded-lg p-4 bg-white"
                 style={{ border: `1px solid ${CHAMPAGNE}44` }}>
              {dm.identity_known ? (
                <>
                  <div className="font-serif text-[15px]" style={{ color: NAVY }}>
                    {dm.name}
                  </div>
                  <div className="text-[12px] mt-1" style={{ color: SLATE }}>
                    {dm.role} · Source : {humanSource(dm.source)}
                  </div>
                  <div className="mt-3 text-[12px] space-y-1"
                       style={{ color: NAVY }}>
                    <div>Email : <span className="font-mono">{dm.email}</span></div>
                    <div>Téléphone : <span className="font-mono">{dm.phone}</span></div>
                    <div>Cadre : <span className="font-mono">{dm.contactability_status}</span></div>
                  </div>
                  {dm.reason && (
                    <p className="mt-2 text-[11px] italic"
                       style={{ color: SLATE }}>{dm.reason}</p>
                  )}
                </>
              ) : (
                <p className="text-[13px]" style={{ color: SLATE }}>
                  {dm.reason
                    || "Aucun décideur nommé trouvé dans les sources connectées."}
                </p>
              )}
            </div>
          </section>

          {/* Evidence list */}
          <section data-testid="evidence-list">
            <h4 className="text-[11px] font-mono uppercase tracking-[0.22em] mb-2"
                style={{ color: SLATE }}>
              Signaux détectés ({evidence.length})
            </h4>
            <div className="space-y-3">
              {evidence.map((e, i) => (
                <div key={i}
                     data-testid={`evidence-item-${i}`}
                     className="rounded-lg p-4 bg-white"
                     style={{ border: `1px solid ${CHAMPAGNE}44` }}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="text-[13px] font-medium"
                           style={{ color: NAVY }}>
                        {humanSignal(e.signal_type)}
                      </div>
                      <div className="text-[11px] font-mono mt-1"
                           style={{ color: SLATE }}>
                        {humanSource(e.source_family)} · Force {e.strength}
                      </div>
                    </div>
                    {e.source_url && (
                      <a href={e.source_url} target="_blank" rel="noreferrer"
                         data-testid={`evidence-link-${i}`}
                         className="text-[11px] flex items-center gap-1"
                         style={{ color: GOLD }}>
                        Source <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                  {e.excerpt && e.excerpt !== "not_found" && (
                    <p className="mt-2 text-[12px]" style={{ color: NAVY }}>
                      {e.excerpt}
                    </p>
                  )}
                  <div className="mt-2 text-[11px] font-mono"
                       style={{ color: SLATE }}>
                    Détecté : {e.detected_at}
                    {e.event_date !== "unknown"
                      && ` · Événement : ${e.event_date}`}
                    {e.reference !== "not_found"
                      && ` · Réf : ${e.reference}`}
                  </div>
                </div>
              ))}
              {evidence.length === 0 && (
                <p className="text-[12px]" style={{ color: SLATE }}>
                  Aucun signal enregistré pour cette entité.
                </p>
              )}
            </div>
          </section>
        </div>
      </aside>
    </div>
  );
}
